import { FirebaseData } from "../../General/FirebaseData";
import { FirebaseInfoField } from "../../General/FirebaseInfoField";
import { IncomeData } from "../Income/IncomeData";
import { FirebaseFile } from "../../General/FirebaseFile";
import { PropertyData } from "../PropertyData";
import { LocalData } from "../../General/LocalData";
import { Utilities } from "../../../../utils/utilities";
import { DataCreator } from "../../../../utils/dataCreator";
import { FirebaseEvents } from "../../../../utils/firebaseEvents";
import { FirestoreError, collection, doc, getDocs, getFirestore, query, setDoc, where } from "firebase/firestore";
import { FileDocumentData } from "../../General/FileDocumentData";
import CollectionLoader from "../../General/CollectionLoader";
import { UnitType } from "./UnitType";
import { UnitInfoFields } from "./UnitInfoFields";
import { FirebaseDataExternalReference } from "./FirebaseDataExternalReference";
import { UserData } from "../../User/UserData";
import { UnitIndexRegulation } from "./UnitIndexRegulation";
import { UnitJointArea } from "./UnitJointArea";
import { TenantType } from "./TenantType";
import { calculateDaysLeftOfContract } from "@src/utils/data/economical/economicalMetrics";
import { generateDocumentID } from "@src/utils/database.utils";

export class UnitData extends FirebaseData {
  property?: PropertyData;
  archived: boolean = false;
  archiveDate?: Date;

  name: string = "";
  tenantType?: FirebaseInfoField<TenantType>;
  /**
   * TODO: Change type name to category, businessType or something more descriptive
   */
  type?: FirebaseInfoField<UnitType>;
  orgNumber?: FirebaseInfoField<string>;
  floors?: FirebaseInfoField<number[]>;
  postAddress?: FirebaseInfoField<string>;
  area?: FirebaseInfoField<number>;
  parkingSpots?: FirebaseInfoField<number>;
  snr?: FirebaseInfoField<string>;
  apartmentNumber?: FirebaseInfoField<string>;
  streetNumber?: FirebaseInfoField<string>;
  fireSafetyResponsibles?: FirebaseInfoField<UserData[]>;
  contactInformation?: FirebaseInfoField<string>;
  contactEmail?: FirebaseInfoField<string>;
  contactPhone?: FirebaseInfoField<string>;
  bedroomCount?: FirebaseInfoField<number>;
  furnitured?: FirebaseInfoField<boolean>;
  incomeData?: FirebaseInfoField<IncomeData>;
  vatApplicable?: FirebaseInfoField<number>;
  receivesEhfInvoice?: FirebaseInfoField<boolean>;
  contractConclusionDate?: FirebaseInfoField<Date>;
  contractStartDate?: FirebaseInfoField<Date>;
  contractEndDate?: FirebaseInfoField<Date>;
  runningRenewal?: FirebaseInfoField<number>;
  optionDuration?: FirebaseInfoField<number>;
  optionDeadline?: FirebaseInfoField<Date>;
  optionNotification?: FirebaseInfoField<Date>;
  indexRegulation?: FirebaseInfoField<UnitIndexRegulation>;
  fixedRegulation?: FirebaseInfoField<number>;
  regulationInterval?: FirebaseInfoField<number>;
  lastRegulation?: FirebaseInfoField<Date>;
  jointArea?: FirebaseInfoField<UnitJointArea>;
  guarantee?: FirebaseInfoField<string>;
  waterRent?: FirebaseInfoField<number>;
  electricityRent?: FirebaseInfoField<number>;
  accountNumber?: FirebaseInfoField<string>;
  marketRent?: FirebaseInfoField<number>;
  note?: FirebaseInfoField<string>;

  files: CollectionLoader<FileDocumentData>;

  externalRefs: { [key: string]: FirebaseDataExternalReference } = {};

  infoFieldsLoaded: boolean = false;
  infoFieldsLoading: boolean = false;
  infoFieldsLoadedListeners: ((error?: FirestoreError) => void)[] = [];

  constructor(id: string, collectionName: string, parent: FirebaseData | undefined) {
    super(id, collectionName, parent);
    this.files = new CollectionLoader("files", this, FileDocumentData);
    if (parent && parent instanceof PropertyData) this.property = parent;
  }

  setup(data: any) {
    if (typeof data.name === "string") this.name = data.name;
    if (typeof data.archived === "boolean") this.archived = data.archived;

    const date = Utilities.getDateFromTimestamp(data.archiveDate);
    if (date) this.archiveDate = date;

    if (typeof data.fields === "object") {
      UnitInfoFields.loadFields(this, data.fields);
    }
    if (typeof data.externalRefs === "object") {
      this.externalRefs = {};
      for (const apiClientID of Object.keys(data.externalRefs)) {
        this.externalRefs[apiClientID] = {
          id: typeof data.externalRefs[apiClientID].id === "string" ? data.externalRefs[apiClientID].id : "",
          apiClientID: apiClientID,
        };
      }
    }

    if (data) {
      super.setup(data);
    }
  }

  saveUnitFile(file: File, completion?: (error?: Error, file?: FirebaseFile) => void) {
    this.saveFile(file, file.name)
      .then((savedFile) => completion?.(undefined, savedFile))
      .catch((error) => completion?.(error));
  }

  saveFile(file: Blob, fullFileName: string, customMimeType?: string): Promise<FirebaseFile> {
    const fileType = file.type.split("/").pop() || fullFileName.split(".").pop() || "unknown";
    let fileName = fullFileName.replace(new RegExp(`.${fileType}$`), "");

    let existingNameCount = this.files.documents.filter((existingFile) => existingFile.name?.startsWith(fileName)).length;

    if (existingNameCount > 0) {
      fileName = `${fileName}(${existingNameCount + 1})`;
    }

    const path = `${this.getCompletePath()}/files/${FirebaseData.generateID()}/`;
    const firebaseFile = new FirebaseFile(`${path}${fileName}.${fileType}`);
    firebaseFile.fileType = fileType;
    firebaseFile.title = fullFileName;
    firebaseFile.id = Utilities.generateUUID().split("-")[0];

    return new Promise<FirebaseFile>((resolve, reject) => {
      firebaseFile.saveImage(
        file,
        (success) => {
          if (success && this.property) {
            const db = getFirestore();
            const uploadFileRef = doc(db, "properties", this.property.id, "units", this.id, "files", firebaseFile.id);

            setDoc(uploadFileRef, {
              path: firebaseFile.path,
              name: firebaseFile.title,
              type: fileType,
              visibility: ["landlordAdmin", "tenantAdmin"],
            })
              .then(() => {
                resolve(firebaseFile);
                FirebaseEvents.logEvent("tenant_file_created", { file_type: firebaseFile.fileType ?? "" });
              })
              .catch(reject);
          } else {
            reject();
          }
        },
        customMimeType
      );
    });
  }

  loadInfoFields(completion: () => void) {
    //console.log("LOADING UNIT INFO FIELDS");
    const loaded = () => {
      this.infoFieldsLoaded = true;
      this.infoFieldsLoading = false;
      this.infoFieldsLoadedListeners.forEach((listener) => {
        listener();
      });
      this.infoFieldsLoadedListeners = [];
    };

    this.infoFieldsLoadedListeners.push(completion);
    if (!this.infoFieldsLoading) {
      let roleArray = [];
      const member = this.property?.members.getData(LocalData.currentUser.id) ?? undefined;
      if (member?.hasLandlord()) roleArray.push(member && member.isAdmin ? "landlordAdmin" : "landlord");
      else if (member?.hasTenant() ?? false) roleArray.push(member && member.isAdmin ? "tenantAdmin" : "tenant");
      else if (member?.hasEntrepreneur() ?? false) roleArray.push("entrepreneur");

      if (roleArray.length > 0 && this.property) {
        this.infoFieldsLoading = true;
        const db = getFirestore();
        const infoFieldsRef = collection(db, "properties", this.property.id, "units", this.id, "infoFields");
        const filteredQuery = query(infoFieldsRef, where("visibility", "array-contains-any", roleArray));

        getDocs(filteredQuery)
          .then((snapshot) => {
            for (let i = 0; i < snapshot.docs.length; i++) {
              const data = snapshot.docs[i].data();
              this.setup(data);
            }
          })
          .catch((error) => {
            console.error("Error loading infofields " + this.id);
            console.error(error);
          })
          .finally(() => loaded());
      } else {
        loaded();
        return;
      }
    }
  }
  getContractValue(): number {
    const contractLength = this.totalContractDays();
    if (contractLength) {
      let totalRent = this.totalRent();
      return Math.max(0, (totalRent / 365) * contractLength);
    } else {
      return 0;
    }
  }

  getRemainingContractSum(): number {
    const contractLength = this.daysLeftOfContract();
    let totalRent = this.totalRent();
    const remainingContractSum = (totalRent / 365) * (contractLength ?? 0);
    if (remainingContractSum >= 0) {
      return remainingContractSum;
    } else {
      return 0;
    }
  }

  getPricePerArea() {
    let annualAreaRent = this.areaRent();
    let area = this.area?.value;
    if (area && annualAreaRent) {
      return annualAreaRent / area;
    }
    return 0;
  }

  totalRent() {
    return this.incomeData?.value?.sumOfIncomes(12) ?? 0;
  }

  areaRent() {
    return this.incomeData?.value?.sumOfAreaRent(12) ?? 0;
  }

  parkingRent() {
    return this.incomeData?.value?.sumOfParkingRent(12) ?? 0;
  }

  commonRent() {
    return this.incomeData?.value?.sumOfCommonRent(12) ?? 0;
  }

  otherRent() {
    return this.incomeData?.value?.sumOfOtherRent(12) ?? 0;
  }

  /**
   * @deprecated use global calculateDaysLeftOfContract instead
   */
  daysLeftOfContract(): number | undefined {
    return calculateDaysLeftOfContract(this.contractEndDate?.value);
  }

  /**
   * @deprecated use global calculateTotalContractDays instead
   */
  totalContractDays(): number | undefined {
    if (this.contractEndDate?.value instanceof Date && this.contractStartDate?.value instanceof Date) {
      const standardizeDate = (date: Date) => {
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);
        return date;
      };
      const endDate = standardizeDate(this.contractEndDate.value);
      const today = standardizeDate(this.contractStartDate.value);
      const msToEndDate = endDate.getTime() - today.getTime();
      return msToEndDate / (1000 * 60 * 60 * 24);
    }
    return undefined;
  }

  deleteTenant(): Promise<void> {
    return this.saveUpdate({
      deletedBy: DataCreator.getDeletedBy(),
    }).then(() => {
      this.deleted = true;
      if (this.property) {
        this.property.units = this.property.units.filter((u) => u !== this);
      }
      return;
    });
  }
}
